import React, { useState, useCallback } from 'react';
import * as I from 'lucide-react';

const S3Uploader = ({ onClose }) => {
    const [files, setFiles] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [uploadProgress, setUploadProgress] = useState({});

    const handleFileSelect = (event) => {
        const selectedFiles = Array.from(event.target.files);
        setFiles(prev => [...prev, ...selectedFiles]);
    };

    const handleRemoveFile = (index) => {
        setFiles(prev => prev.filter((_, i) => i !== index));
    };

    const handleUpload = async () => {
        if (files.length === 0) return;

        setUploading(true);
        const progress = {};

        try {
            for (let i = 0; i < files.length; i++) {
                const file = files[i];
                progress[i] = 0;
                setUploadProgress({...progress});

                // Create form data for file upload
                const formData = new FormData();
                formData.append('file', file);

                // Upload to S3 via backend
                const uploadUrl = '/api/manual-invoices/upload';
                    
                const xhr = new XMLHttpRequest();
                
                // Track upload progress
                xhr.upload.addEventListener('progress', (event) => {
                    if (event.lengthComputable) {
                        progress[i] = Math.round((event.loaded * 100) / event.total);
                        setUploadProgress({...progress});
                    }
                });

                // Create a promise to handle the XHR request
                const uploadPromise = new Promise((resolve, reject) => {
                    xhr.onload = () => {
                        if (xhr.status >= 200 && xhr.status < 300) {
                            resolve(xhr.response);
                        } else {
                            reject(new Error(`Upload failed with status ${xhr.status}: ${xhr.statusText}`));
                        }
                    };
                    xhr.onerror = () => reject(new Error('Upload failed due to network error'));
                });

                // Send the request
                xhr.open('POST', uploadUrl);
                xhr.send(formData);
                
                const response = await uploadPromise;
                
                // Parse the JSON response
                const result = JSON.parse(response);
                
                if (result.success) {
                    progress[i] = 100;
                    setUploadProgress({...progress});
                    console.log(`Successfully uploaded ${file.name} to S3:`, result);
                } else {
                    throw new Error(`Upload failed for ${file.name}: ${result.message || 'Unknown error'}`);
                }
            }

            // Clear files after successful upload
            setTimeout(() => {
                setFiles([]);
                setUploadProgress({});
                setUploading(false);
            }, 2000);

        } catch (error) {
            console.error('Upload error:', error);
            setUploading(false);
        }
    };

    return (
        <div className="flex flex-col h-full">
            {/* Upload Area */}
            <div className="flex-1 p-6">
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-blue-400 transition-colors">
                    <I.CloudUpload className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">
                        Upload Invoice Files to S3
                    </h3>
                    <p className="text-gray-600 mb-4">
                        Drag and drop files here, or click to select files
                    </p>
                    <p className="text-xs text-gray-500 mb-4">
                        Files will be uploaded to AWS S3 storage
                    </p>
                    <input
                        type="file"
                        multiple
                        accept=".pdf,.jpg,.jpeg,.png,.doc,.docx"
                        onChange={handleFileSelect}
                        className="hidden"
                        id="file-upload"
                        disabled={uploading}
                    />
                    <label
                        htmlFor="file-upload"
                        className="inline-flex items-center px-4 py-2 bg-blue-600 text-white font-medium rounded-lg hover:bg-blue-700 cursor-pointer disabled:opacity-50"
                    >
                        <I.Upload className="mr-2 w-4 h-4" />
                        Select Files
                    </label>
                </div>

                {/* File List */}
                {files.length > 0 && (
                    <div className="mt-6">
                        <h4 className="font-medium text-gray-900 mb-3">Selected Files:</h4>
                        <div className="space-y-2">
                            {files.map((file, index) => (
                                <div key={index} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                                    <div className="flex items-center flex-1">
                                        <I.File className="w-5 h-5 text-gray-400 mr-3" />
                                        <div className="flex-1">
                                            <p className="text-sm font-medium text-gray-900">{file.name}</p>
                                            <p className="text-xs text-gray-500">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
                                        </div>
                                    </div>
                                    {uploading && (
                                        <div className="flex items-center mr-3">
                                            <div className="w-24 bg-gray-200 rounded-full h-2 mr-2">
                                                <div
                                                    className="bg-blue-600 h-2 rounded-full transition-all"
                                                    style={{ width: `${uploadProgress[index] || 0}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-gray-600">
                                                {uploadProgress[index] || 0}%
                                            </span>
                                        </div>
                                    )}
                                    {!uploading && (
                                        <button
                                            onClick={() => handleRemoveFile(index)}
                                            className="text-red-500 hover:text-red-700"
                                        >
                                            <I.X className="w-4 h-4" />
                                        </button>
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                )}
            </div>

            {/* Action Buttons */}
            <div className="border-t p-4 flex justify-end space-x-3">
                <button
                    onClick={onClose}
                    disabled={uploading}
                    className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50"
                >
                    Cancel
                </button>
                {files.length > 0 && (
                    <button
                        onClick={handleUpload}
                        disabled={uploading}
                        className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center"
                    >
                        {uploading ? (
                            <>
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2" />
                                Uploading to S3...
                            </>
                        ) : (
                            <>
                                <I.Upload className="mr-2 w-4 h-4" />
                                Upload {files.length} File{files.length > 1 ? 's' : ''} to S3
                            </>
                        )}
                    </button>
                )}
            </div>
        </div>
    );
};

export default S3Uploader;
